import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Building } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  { key: 'abn', label: 'ABN', type: 'text', placeholder: '12 345 678 901' },
  { key: 'acn', label: 'ACN', type: 'text', placeholder: '123 456 789' },
  { key: 'entity_type', label: 'Entity Type', type: 'select', options: ['Company', 'Sole Trader', 'Partnership', 'Trust'] },
  { key: 'legal_name', label: 'Legal Name', type: 'text' },
  { key: 'trading_name', label: 'Trading Name', type: 'text' },
  { key: 'gst_registered', label: 'GST Registered', type: 'select', options: ['true', 'false'] },
  { key: 'industry', label: 'Industry', type: 'text' },
  { key: 'years_trading', label: 'Years Trading', type: 'select', options: ['0-1', '1-2', '2-5', '5-10', '10+'] },
  { key: 'annual_revenue', label: 'Annual Revenue', type: 'select', options: ['<$100k', '$100k-$500k', '$500k-$1M', '$1M-$5M', '$5M+'] },
  { key: 'address', label: 'Address', type: 'text' },
  { key: 'state', label: 'State', type: 'select', options: ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] },
  { key: 'postcode', label: 'Postcode', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website', label: 'Website', type: 'text' },
];

export default function BusinessTab({ deal, dealId, onRefresh }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);

  useEffect(() => {
    const biz = deal.business || {};
    // Pre-fill from deal if business is empty
    const initial = {
      abn: biz.abn || deal.abn || '',
      entity_type: biz.entity_type || deal.entity_type || '',
      legal_name: biz.legal_name || deal.business_name || '',
      trading_name: biz.trading_name || deal.business_name || '',
      industry: biz.industry || deal.industry || '',
      years_trading: biz.years_trading || deal.years_trading || '',
      gst_registered: biz.gst_registered != null ? String(biz.gst_registered) : (deal.gst_registered != null ? String(deal.gst_registered) : ''),
      ...Object.fromEntries(FIELDS.filter(f => !['abn','entity_type','legal_name','trading_name','industry','years_trading','gst_registered'].includes(f.key)).map(f => [f.key, biz[f.key] || '']))
    };
    setForm(initial);
    setHasExisting(!!biz.id);
  }, [deal]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, gst_registered: form.gst_registered === 'true' };
      await axios.put(`${API}/crm/deals/${dealId}/business`, payload);
      toast.success('Business details saved');
      onRefresh();
    } catch (error) {
      toast.error('Failed to save business details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="business-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2"><Building className="h-5 w-5 text-purple-400" /> Business Details</h2>
        <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-business-btn"
          className="bg-purple-600 hover:bg-purple-700 text-white">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map(f => (
          <div key={f.key} className={f.key === 'address' ? 'md:col-span-2' : ''}>
            <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
            {f.type === 'select' ? (
              <select value={form[f.key] || ''} onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                data-testid={`business-${f.key}`}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
                <option value="">Select...</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input type={f.type} value={form[f.key] || ''} placeholder={f.placeholder || ''}
                onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                data-testid={`business-${f.key}`}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
